// components/rjsf-customizations/CustomWidgets.tsx
import React, {useMemo} from 'react';
import { Input } from "@/components/ui/input";
// import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
// import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

// ייבוא טיפוסים רלוונטיים מ-RJSF
import { WidgetProps, StrictRJSFSchema, UiSchema } from '@rjsf/utils';
import {FormControl} from "@/components/ui/form.tsx";
import {Textarea} from "@/components/ui/textarea.tsx";
import {logger} from "@sentry/react";
import MultipleSelector, {Option} from "@/components/ui/multi-select.tsx";

/**
 * ווידג'ט קלט טקסט/מספר/אינטגר מותאם אישית (Input של Shadcn)
 */
export function CustomInputWidget<T = any, S extends StrictRJSFSchema = StrictRJSFSchema, U extends UiSchema = UiSchema>(props: WidgetProps<T, S, U>) {
    const { id, placeholder, value, required, disabled, readonly, onChange, onBlur, onFocus, options, type, schema } = props;

    const inputValue = value === null || value === undefined ? "" : String(value); // וודא שהערך הוא סטרינג עבור קלט HTML

    const _onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        if (schema.type === "number" || schema.type === "integer") {
            const parsedValue = parseFloat(newValue);
            // RJSF expects number | undefined for number/integer fields
            onChange(isNaN(parsedValue) ? (options.emptyValue as any) : parsedValue);
        } else {
            // RJSF expects string | undefined for string fields
            onChange(newValue === "" ? (options.emptyValue as any) : newValue);
        }
    };

    const _onBlur = (event: React.FocusEvent<HTMLInputElement>) => onBlur(id, event.target.value);
    const _onFocus = (event: React.FocusEvent<HTMLInputElement>) => onFocus(id, event.target.value);

    // type: האם זה number, integer, או string (ברירת מחדל אם לא הוגדר format)
    const inputType = schema.type === "string" ? (schema.format || "text") : schema.type;


    return (
        <Input
            id={id}
            placeholder={placeholder}
            value={inputValue}
            required={required}
            disabled={disabled || readonly}
            onChange={_onChange}
            onBlur={_onBlur}
            className="h-9"
            onFocus={_onFocus}
            type={inputType === "integer" ? "number" : inputType as React.HTMLInputTypeAttribute} // מפה לטיפוס HTML input
        />
    );
}

/**
 * ווידג'ט Textarea מותאם אישית (Textarea של Shadcn)
 */
export function CustomTextareaWidget<T = any, S extends StrictRJSFSchema = StrictRJSFSchema, U extends UiSchema = UiSchema>(props: WidgetProps<T, S, U>) {
    const { id, placeholder, value, required, disabled, readonly, onChange, onBlur, onFocus, options } = props;

    const textareaValue = value === null || value === undefined ? "" : String(value);

    const _onChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = event.target.value;
        onChange(newValue === "" ? (options.emptyValue as any) : newValue);
    };

    const _onBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => onBlur(id, event.target.value);
    const _onFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => onFocus(id, event.target.value);

    return (
        <Textarea
            id={id}
            placeholder={placeholder}
            value={textareaValue}
            required={required}
            disabled={disabled || readonly}
            onChange={_onChange}
            onBlur={_onBlur}
            onFocus={_onFocus}
        />
    );
}


/**
 * ווידג'ט Checkbox מותאם אישית (Checkbox של Shadcn)
 * משמש עבור boolean schema
 */
export function CustomCheckboxWidget<T = any, S extends StrictRJSFSchema = StrictRJSFSchema, U extends UiSchema = UiSchema>(props: WidgetProps<T, S, U>) {
    const { id, value, required, disabled, readonly, onChange } = props;

    const _onCheckedChange = (checkedState: boolean) => {
        onChange(checkedState);
    };

    return (
        <Checkbox
            id={id}
            checked={Boolean(value)} // RJSF can pass undefined/null, ensure boolean
            required={required}
            disabled={disabled || readonly}
            onCheckedChange={_onCheckedChange}
        />
    );
}

/**
 * ווידג'ט Select מותאם אישית (Select של Shadcn)
 */
export function CustomSelectWidget<T = any, S extends StrictRJSFSchema = StrictRJSFSchema, U extends UiSchema = UiSchema>(props: WidgetProps<T, S, U>) {
    const { id, options: _options, value, required, disabled, readonly, placeholder, onChange, multiple } = props;
    const { enumOptions, /* enumDisabled */ } = _options;

    const _onValueChange = (selectedValue: Option[] | string) => {
        // RJSF expects onChange(string | number | boolean | undefined).
        // We need to find the original value type from enumOptions

        if(!multiple && selectedValue === "@@@clear-select@@@") {
            onChange(""); // Clear the value
            return;
        }

        if(typeof selectedValue === "string") {
            const selectedOption = options.find(opt => String(opt.value) === selectedValue);
            onChange(selectedOption?.value ?? _options.emptyValue);
            return;
        }

        onChange(selectedValue.map(i => i.value));
    };

    const emptyValueLabel: () => (string|null) = ()=> {

        if(multiple) return null;

        const index = enumOptions.findIndex(opt => String(opt.value) === "");

        if(index === -1) {
            return null;
        }

        return props.schema.options?.enum_titles?.[index] ?? enumOptions?.[index]?.label ?? null;
    }

    const options = useMemo(() => {
        const opts = enumOptions.map((opt, i) => ({
            value: String(opt.value),
            label: props.schema.options?.enum_titles?.[i] || opt.label
        }));

        if(multiple) {
            return opts.filter(opt => opt.value);
        }

        return opts;

    }, [enumOptions, props.schema.options?.enum_titles]);

    function getValues() {
        if (Array.isArray(value)) {
            return (options ?? [])?.filter(opt => value.includes(opt.value));
        }
        return [];
    }

    if(multiple) {
        return <MultipleSelector
            value={getValues()} // Ensure value is an array
            options={options}
            onChange={values => _onValueChange(values)}
            disabled={disabled || readonly}
            badgeClassName="rounded-full"
            hideClearAllButton={required}
            emptyIndicator={<div className="text-gray-400">
                לא נמצאו אפשרויות לבחירה
            </div>}
            hidePlaceholderWhenSelected
            placeholder={emptyValueLabel() ?? (placeholder ? placeholder : 'בחר...')}
        />
    }


    return (
        <Select
            value={String(value)} // Shadcn expects string value
            onValueChange={_onValueChange}
            disabled={disabled || readonly}
            required={required}
            dir="rtl"
        >
            <FormControl>
                <SelectTrigger id={id} className={cn(!value && !placeholder && "text-muted-foreground", "w-full")}>
                    <SelectValue placeholder={ emptyValueLabel() ?? (placeholder ? placeholder : 'בחר...')} />
                </SelectTrigger>
            </FormControl>
            <SelectContent className="w-full">
                {options.map(({ value: optionValue, label: optionLabel }, i) => {
                    return (
                        <SelectItem key={i} value={String(optionValue) === "" ? "@@@clear-select@@@" : String(optionValue)}>
                            {optionLabel}
                        </SelectItem>
                    );
                })}
            </SelectContent>
        </Select>
    );
}


/**
 * ווידג'ט Radio מותאם אישית (RadioGroup של Shadcn)
 */
export function CustomRadioWidget<T = any, S extends StrictRJSFSchema = StrictRJSFSchema, U extends UiSchema = UiSchema>(props: WidgetProps<T, S, U>) {
    const { id, options, value, required, disabled, readonly, onChange } = props;
    const { enumOptions, /* enumDisabled */ } = options;

    const _onValueChange = (selectedValueString: string) => {
        // RJSF expects onChange(string | number | boolean | undefined).
        // Find the original value type from enumOptions
        const selectedOption = enumOptions.find(opt => String(opt.value) === selectedValueString);
        onChange(selectedOption ? selectedOption.value : options.emptyValue); // Return original type or emptyValue
    };

    return (
        <RadioGroup
            id={id}
            value={value === undefined || value === null ? "" : String(value)} // Convert value to string for RadioGroup
            onValueChange={_onValueChange}
            required={required}
            disabled={disabled || readonly}
            className="flex flex-col space-y-1"
        >
            {enumOptions.map(({ value: optionValue, label: optionLabel }, i) => {
                // TODO: handle enumDisabled if needed
                const itemValueString = String(optionValue);
                return (
                    <div key={i} className="flex items-center space-x-2">
                        <RadioGroupItem value={itemValueString} id={`${id}-${i}`} disabled={disabled || readonly} />
                        <Label htmlFor={`${id}-${i}`}>{optionLabel}</Label>
                    </div>
                );
            })}
        </RadioGroup>
    );
}

/**
 * ווידג'ט Checkboxes מותאם אישית (כמה Checkbox של Shadcn)
 */
export function CustomCheckboxesWidget<T = any, S extends StrictRJSFSchema = StrictRJSFSchema, U extends UiSchema = UiSchema>(props: WidgetProps<T, S, U>) {
    const { id, options, value, required, disabled, readonly, onChange } = props;
    const { enumOptions, /* enumDisabled */ } = options;
    // value is an array of selected values (e.g., ["val1", "val3"])

    const _onCheckedChange = (checkedState: boolean, optionValue: any) => {
        let newValue = Array.isArray(value) ? [...value] : [];

        if (checkedState) {
            // Add value if checked and not already present
            if (!newValue.includes(optionValue)) {
                newValue.push(optionValue);
            }
        } else {
            // Remove value if unchecked
            newValue = newValue.filter(v => v !== optionValue);
        }

        // RJSF CheckboxesWidget expects onChange(array of values | undefined)
        onChange(newValue.length === 0 ? (options.emptyValue as any) : newValue);
    };


    return (
        <div>
            {enumOptions.map(({ value: optionValue, label: optionLabel }, i) => {
                // TODO: handle enumDisabled if needed
                const isChecked = Array.isArray(value) && value.includes(optionValue);
                const itemCheckboxId = `${id}_${i}`;

                return (
                    <div key={i} className="flex items-center space-x-2 mb-1">
                        <Checkbox
                            id={itemCheckboxId}
                            checked={isChecked}
                            onCheckedChange={(checkedState) => _onCheckedChange(checkedState, optionValue)}
                            disabled={disabled || readonly}
                            // required={required} // Note: RJSF handles overall requiredness
                        />
                        <Label htmlFor={itemCheckboxId}>{optionLabel}</Label>
                    </div>
                );
            })}
        </div>
    );
}


// אובייקט הממפה את שמות הווידג'טים הסטנדרטיים של RJSF לרכיבים המותאמים אישית שלנו
export const customWidgets = {
    TextWidget: CustomInputWidget,
    PasswordWidget: CustomInputWidget,
    EmailWidget: CustomInputWidget,
    URLWidget: CustomInputWidget,
    DateWidget: CustomInputWidget, // Consider a dedicated Date Picker widget
    DateTimeWidget: CustomInputWidget, // Consider a dedicated DateTime Picker widget
    TimeWidget: CustomInputWidget,
    UpDownWidget: CustomInputWidget,
    RangeWidget: CustomInputWidget, // Consider a dedicated Slider widget
    TextareaWidget: CustomTextareaWidget,
    CheckboxWidget: CustomCheckboxWidget,
    SelectWidget: CustomSelectWidget,
    RadioWidget: CustomRadioWidget,
    CheckboxesWidget: CustomCheckboxesWidget,
    // Add other widgets as needed
};